import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Card, Col, Row, ProgressBar, Button, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ExportModal from 'components/common/ExportModal';
import {
  FILTER_FIELD_STYLE,
  FormProvider,
  RHFControl,
  RHFSelect
} from 'components/common/form';
import SoftBadge from 'components/common/SoftBadge';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, addMonths } from 'date-fns';
import { formatMoney } from 'helpers/utils';
import {
  useSponsorshipsForManagement,
  useSponsorshipDetail,
  useEndSponsorship,
  useChangeSponsorshipPayment
} from 'hooks/useBeneficiaries';
import { useAuthContext } from 'context/authContext';
import LoadingContainer from 'components/common/LoadingContainer';
import useBranchPermission from 'hooks/useBranchPermission';
import SelectDonationModal from './sponsorships/sponsorship-form/SelectDonationModal';
import paths from 'routes/paths';

// Stats Card Component
const StatCard = ({
  icon,
  title,
  value,
  subtitle,
  details,
  color = 'primary',
  onClick,
  active
}) => (
  <Card
    className={`h-100 ${onClick ? 'cursor-pointer' : ''} ${
      active ? 'border-' + color : ''
    }`}
    onClick={onClick}
    style={{
      transition: 'transform 0.2s',
      borderWidth: active ? '2px' : '1px'
    }}
    onMouseEnter={e =>
      onClick && (e.currentTarget.style.transform = 'translateY(-2px)')
    }
    onMouseLeave={e =>
      onClick && (e.currentTarget.style.transform = 'translateY(0)')
    }
  >
    <Card.Body>
      <Flex justifyContent="between" alignItems="center">
        <div className="me-2">
          <h6 className="text-700 mb-1">{title}</h6>
          <h2 className={`text-${color} mb-0`}>{value}</h2>
          {details && <p className="text-500 fs--1 mb-0 mt-1">{details}</p>}
          {subtitle && <p className="text-400 fs--2 mb-0 mt-1">{subtitle}</p>}
        </div>
        <div className={`icon-circle icon-circle-${color} flex-shrink-0`}>
          <FontAwesomeIcon icon={icon} className="fs-2" />
        </div>
      </Flex>
    </Card.Body>
  </Card>
);

// Detailed Sponsorship View Modal
const SponsorshipDetailModal = ({
  show,
  onHide,
  beneficiaryId,
  sponsorshipTypeId, // The specific beneficiary_sponsorship_type.id to show
  onAddSponsor,
  onEndSponsorship,
  onChangePayment
}) => {
  const { sponsorshipDetail, isLoading } = useSponsorshipDetail(beneficiaryId);

  if (!beneficiaryId) return null;

  if (isLoading) {
    return (
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Body className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (!sponsorshipDetail) return null;

  const { beneficiary, sponsorshipsByType } = sponsorshipDetail;
  const fullName = `${beneficiary.first_name} ${
    beneficiary.middle_name || ''
  } ${beneficiary.last_name}`.trim();

  // Filter to only the specific sponsorship type if provided
  const filteredTypes = sponsorshipTypeId
    ? Object.values(sponsorshipsByType).filter(
        t => t.sponsorshipType.id === sponsorshipTypeId
      )
    : Object.values(sponsorshipsByType);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Sponsorship Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          {/* Header Section */}
          <Col xs={12}>
            <Card className="bg-light">
              <Card.Body>
                <Flex alignItems="start">
                  <Avatar size="3xl" name={fullName} className="me-3" />
                  <div className="flex-1">
                    <h5 className="mb-1">{fullName}</h5>
                    <p className="mb-1 text-600">
                      <strong>ID:</strong> B{beneficiary.simple_id}
                    </p>
                    <p className="mb-1 text-600">
                      <strong>Location:</strong>{' '}
                      {beneficiary.country || 'Unknown'}
                    </p>
                    <p className="mb-1 text-600">
                      <strong>Gender:</strong> {beneficiary.gender || '-'}
                    </p>
                  </div>
                </Flex>
              </Card.Body>
            </Card>
          </Col>

          {/* Sponsorship Types Breakdown */}
          {filteredTypes.map(typeData => {
            const {
              sponsorshipType,
              activeSponsorships,
              totalCoverage,
              monthlyAmountCovered,
              monthlyAmountNeeded
            } = typeData;

            return (
              <Col xs={12} key={sponsorshipType.id}>
                <Card>
                  <Card.Body>
                    <Flex
                      justifyContent="between"
                      alignItems="center"
                      className="mb-3"
                    >
                      <h6 className="mb-0">
                        {sponsorshipType.sponsorship_type?.type ||
                          'Sponsorship'}
                        <span className="text-500 ms-2">
                          ({formatMoney(sponsorshipType.cost)}/month)
                        </span>
                      </h6>
                      <div>
                        {totalCoverage >= 100 ? (
                          <SoftBadge bg="success">Fully Sponsored</SoftBadge>
                        ) : totalCoverage > 0 ? (
                          <SoftBadge bg="warning">
                            {totalCoverage}% Sponsored
                          </SoftBadge>
                        ) : (
                          <SoftBadge bg="danger">Unsponsored</SoftBadge>
                        )}
                      </div>
                    </Flex>

                    {/* Progress Bar */}
                    <ProgressBar className="mb-3" style={{ height: '10px' }}>
                      <ProgressBar
                        variant="success"
                        now={totalCoverage}
                        key={1}
                      />
                    </ProgressBar>

                    {/* Donors for this type */}
                    {activeSponsorships.length > 0 ? (
                      activeSponsorships.map((sponsorship, sIdx) => {
                        // Monthly-funded sponsorships are ongoing - no end date
                        const isMonthly =
                          sponsorship.order_detail?.billing_condition ===
                          'monthly';
                        const endDate =
                          !isMonthly &&
                          sponsorship.start_date &&
                          sponsorship.duration
                            ? addMonths(
                                new Date(sponsorship.start_date),
                                sponsorship.duration
                              )
                            : null;

                        return (
                          <div
                            key={sponsorship.id}
                            className={sIdx > 0 ? 'mt-3 pt-3 border-top' : ''}
                          >
                            <Flex justifyContent="between" alignItems="center">
                              <div>
                                <p className="mb-0 fw-medium">
                                  {sponsorship.donor
                                    ? `${sponsorship.donor.first_name} ${sponsorship.donor.last_name}`
                                    : 'External donor — managed by main branch'}
                                </p>
                                <p className="mb-0 text-500 fs--1">
                                  {sponsorship.coverage_percentage}% coverage
                                </p>
                                {isMonthly ? (
                                  <p className="mb-0 text-success fs--1">
                                    Ongoing since{' '}
                                    {sponsorship.start_date
                                      ? format(
                                          new Date(sponsorship.start_date),
                                          'MMM yyyy'
                                        )
                                      : '—'}
                                  </p>
                                ) : (
                                  endDate && (
                                    <p className="mb-0 text-500 fs--1">
                                      Ends: {format(endDate, 'dd MMM yyyy')}
                                    </p>
                                  )
                                )}
                              </div>
                              <div className="text-end">
                                <p className="mb-0 fw-bold">
                                  {formatMoney(
                                    (sponsorshipType.cost *
                                      sponsorship.coverage_percentage) /
                                      100
                                  )}
                                  /month
                                </p>
                                <div className="d-flex gap-3 justify-content-end">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    onClick={() =>
                                      onChangePayment(sponsorship.id)
                                    }
                                  >
                                    Change payment
                                  </Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() =>
                                      onEndSponsorship(sponsorship.id)
                                    }
                                  >
                                    End
                                  </Button>
                                </div>
                              </div>
                            </Flex>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-500 text-center mb-0">
                        No sponsors yet
                      </p>
                    )}

                    {/* Summary */}
                    <hr />
                    <Flex justifyContent="between" alignItems="center">
                      <div>
                        <p className="mb-0 text-500">
                          Covered: {formatMoney(monthlyAmountCovered)}/month
                        </p>
                        {monthlyAmountNeeded > 0 && (
                          <p className="mb-0 text-danger">
                            Needed: {formatMoney(monthlyAmountNeeded)}/month
                          </p>
                        )}
                      </div>
                    </Flex>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}

          {/* Actions */}
          <Col xs={12}>
            <Flex justifyContent="end" className="gap-2">
              <Button variant="primary" size="sm" onClick={onAddSponsor}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                Add Sponsor
              </Button>
              <Button
                variant="outline-dark"
                size="sm"
                onClick={() => {
                  // TODO: Generate and download PDF report
                  console.log('Download report');
                }}
              >
                <FontAwesomeIcon icon="download" className="me-1" />
                Report
              </Button>
            </Flex>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

StatCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  title: PropTypes.string,
  value: PropTypes.node,
  subtitle: PropTypes.string,
  details: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  active: PropTypes.bool
};

SponsorshipDetailModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  beneficiaryId: PropTypes.string,
  sponsorshipTypeId: PropTypes.string,
  onAddSponsor: PropTypes.func.isRequired,
  onEndSponsorship: PropTypes.func.isRequired,
  onChangePayment: PropTypes.func.isRequired
};

const SponsorshipManagement = () => {
  const navigate = useNavigate();
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  // #2 filters (multi-select, AND across filters, OR within)
  const [listFilters, setListFilters] = useState({
    branch: [],
    type: [],
    childStatus: [],
    donor: ''
  });
  const filterForm = useForm({
    defaultValues: { branch: [], type: [], childStatus: [], donor: '' }
  });
  const watchedFilters = filterForm.watch();
  const watchedFiltersKey = JSON.stringify(watchedFilters);
  useEffect(() => {
    setListFilters(watchedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFiltersKey]);

  // Fetch sponsorships data
  const {
    sponsorships: allSponsorships,
    isLoading: sponsorshipsLoading,
    refetch
  } = useSponsorshipsForManagement();

  const { endSponsorshipAsync } = useEndSponsorship();
  const { changePayment } = useChangeSponsorshipPayment();
  const { user } = useAuthContext();
  const { isMainBranch } = useBranchPermission();
  const [changePaymentFor, setChangePaymentFor] = useState(null);

  // Dashboard boxes: live counts of CHILDREN per status, split by sponsorship
  // type. A child appears once per type they are registered for.
  const DASHBOARD_STATUSES = [
    {
      name: 'Urgent Sponsor Needed',
      color: 'danger',
      icon: 'exclamation-triangle'
    },
    { name: 'Partial Sponsor Needed', color: 'warning', icon: 'user-friends' },
    { name: 'Ready For Sponsorship', color: 'success', icon: 'child' },
    { name: 'Fully Sponsored', color: 'primary', icon: 'check-circle' }
  ];

  const stats = useMemo(() => {
    // "Child Sponsorship (Not Orphan)" must not count as an orphan type
    const isOrphan = t => /orphan/i.test(t || '') && !/not orphan/i.test(t || '');
    const isHafiz = t => /hafiz|hifz/i.test(t || '');
    // "Child Sponsorship" (non-orphan children) - "Orphan Sponsorship" must
    // not match
    const isChild = t => /child/i.test(t || '') && !isOrphan(t);
    const byStatus = {};
    DASHBOARD_STATUSES.forEach(st => {
      byStatus[st.name] = {
        orphan: new Set(),
        hafiz: new Set(),
        child: new Set(),
        byCountry: {}
      };
    });
    allSponsorships.forEach(row => {
      const bucket = byStatus[row.childStatus];
      if (!bucket) return;
      if (isOrphan(row.sponsorshipType)) bucket.orphan.add(row.beneficiaryId);
      if (isHafiz(row.sponsorshipType)) bucket.hafiz.add(row.beneficiaryId);
      if (isChild(row.sponsorshipType)) bucket.child.add(row.beneficiaryId);
      // Per-country breakdown (distinct children), split by type like the
      // headline figure
      const country = row.country || 'Unknown';
      const byType = (bucket.byCountry[country] = bucket.byCountry[country] || {
        all: new Set(),
        orphan: new Set(),
        hafiz: new Set(),
        child: new Set()
      });
      byType.all.add(row.beneficiaryId);
      if (isOrphan(row.sponsorshipType)) byType.orphan.add(row.beneficiaryId);
      if (isHafiz(row.sponsorshipType)) byType.hafiz.add(row.beneficiaryId);
      if (isChild(row.sponsorshipType)) byType.child.add(row.beneficiaryId);
    });
    return Object.fromEntries(
      Object.entries(byStatus).map(([name, sets]) => [
        name,
        {
          orphan: sets.orphan.size,
          hafiz: sets.hafiz.size,
          child: sets.child.size,
          countries: Object.entries(sets.byCountry)
            .map(([country, byType]) => ({
              country,
              count: byType.all.size,
              orphan: byType.orphan.size,
              hafiz: byType.hafiz.size,
              child: byType.child.size
            }))
            .sort((a, b) => b.count - a.count)
        }
      ])
    );
  }, [allSponsorships]);
  // The Child figure only appears once a child is registered on that type
  const showChildCount = Object.values(stats).some(s => s.child > 0);

  // Filter rows by the child's status (clicking a box)
  const sponsorships = useMemo(() => {
    const donorTerm = (listFilters.donor || '').trim().toLowerCase();
    return allSponsorships.filter(s => {
      if (statusFilter !== 'all' && s.childStatus !== statusFilter)
        return false;
      if (
        listFilters.branch?.length &&
        !listFilters.branch.includes(s.branchName)
      )
        return false;
      if (
        listFilters.type?.length &&
        !listFilters.type.includes(s.sponsorshipType)
      )
        return false;
      if (
        listFilters.childStatus?.length &&
        !listFilters.childStatus.includes(s.childStatus)
      )
        return false;
      if (
        donorTerm &&
        !(s.donors || []).some(d =>
          `${d.first_name || ''} ${d.last_name || ''}`
            .toLowerCase()
            .includes(donorTerm)
        )
      )
        return false;
      return true;
    });
  }, [allSponsorships, statusFilter, listFilters]);

  const optionsFrom = key =>
    [...new Set(allSponsorships.map(r => r[key]).filter(Boolean))]
      .sort()
      .map(v => ({ label: v, value: v }));
  const branchOptions = useMemo(
    () => optionsFrom('branchName'),
    [allSponsorships]
  );
  const typeOptions = useMemo(
    () => optionsFrom('sponsorshipType'),
    [allSponsorships]
  );
  const childStatusOptions = useMemo(
    () => optionsFrom('childStatus'),
    [allSponsorships]
  );
  const hasListFilters =
    listFilters.branch?.length ||
    listFilters.type?.length ||
    listFilters.childStatus?.length ||
    listFilters.donor;

  // Export rows: donor + beneficiary info, flattened for CSV/XLSX
  const exportColumns = [
    { accessor: 'name', Header: 'Beneficiary' },
    { accessor: 'ref', Header: 'Beneficiary ref' },
    { accessor: 'country', Header: 'Country' },
    { accessor: 'branchName', Header: 'Branch' },
    { accessor: 'sponsorshipType', Header: 'Sponsorship type' },
    { accessor: 'monthlyCost', Header: 'Monthly cost' },
    { accessor: 'childStatus', Header: 'Child status' },
    { accessor: 'totalCoverage', Header: 'Coverage %' },
    { accessor: 'donorNames', Header: 'Donor(s)' },
    { accessor: 'donorEmails', Header: 'Donor email(s)' },
    { accessor: 'startDateText', Header: 'Start date' },
    { accessor: 'endDateText', Header: 'End date' }
  ];
  const exportData = useMemo(
    () =>
      sponsorships.map(r => ({
        ...r,
        ref: `B${r.simpleId}`,
        donorNames: (r.donors || [])
          .map(d => `${d.first_name} ${d.last_name}`)
          .join('; '),
        donorEmails: (r.donors || [])
          .map(d => d.primary_email)
          .filter(Boolean)
          .join('; '),
        startDateText: r.startDate
          ? format(new Date(r.startDate), 'dd/MM/yyyy')
          : '',
        endDateText: r.ongoing
          ? 'Ongoing'
          : r.endDate
          ? format(new Date(r.endDate), 'dd/MM/yyyy')
          : ''
      })),
    [sponsorships]
  );

  const isLoading = sponsorshipsLoading;

  // Handle ending a sponsorship
  const handleEndSponsorship = async sponsorshipId => {
    if (window.confirm('Are you sure you want to end this sponsorship?')) {
      try {
        await endSponsorshipAsync(sponsorshipId);
        refetch();
      } catch (error) {
        console.error('Error ending sponsorship:', error);
      }
    }
  };

  // Change payment swaps the detail modal for the donation picker, then
  // returns to the detail modal (refreshed via query invalidation).
  const handleChangePaymentClick = sponsorshipId => {
    setShowDetailModal(false);
    setChangePaymentFor(sponsorshipId);
  };

  const handleChangePaymentSubmit = donation => {
    changePayment(
      {
        sponsorshipId: changePaymentFor,
        newOrderDetailId: donation.orderDetailId,
        actor: {
          userId: user?.id,
          userName:
            `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
            user?.email
        }
      },
      { onSuccess: () => refetch() }
    );
  };

  if (isLoading) return <LoadingContainer />;

  const columns = [
    {
      accessor: 'name',
      Header: 'Beneficiary',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const { name, simpleId, beneficiaryId } = row.original;
        return (
          <Link
            to={paths.beneficiaryProfile.replace(':id', beneficiaryId)}
            className="text-inherit"
          >
            <Flex alignItems="center">
              <Avatar size="xl" name={name} />
              <div className="ms-2">
                <p className="mb-0 fw-medium text-primary">{name}</p>
                <p className="mb-0 text-500 fs--1">B{simpleId}</p>
              </div>
            </Flex>
          </Link>
        );
      }
    },
    {
      accessor: 'donorCount',
      Header: 'Donor(s)',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const { donorCount, donors } = row.original;
        if (donorCount === 0)
          return <span className="text-500">No sponsor</span>;
        // Donors outside the viewer's branch are hidden by RLS: they're in
        // donorCount but missing from the embedded donor list
        const externalCount = donorCount - donors.filter(d => d?.id).length;
        return (
          <div>
            {donors.map(donor =>
              donor?.id ? (
                <Link
                  key={donor.id}
                  to={paths.donorDetails.replace(':id', donor.id)}
                  className="d-block"
                >
                  {donor.first_name} {donor.last_name}
                </Link>
              ) : null
            )}
            {externalCount > 0 && (
              <span className="d-block text-600" title="Managed by main branch">
                {externalCount === 1
                  ? 'External donor'
                  : `${externalCount} external donors`}
              </span>
            )}
          </div>
        );
      }
    },
    // HQ views across branches, so show which branch each child belongs to
    ...(isMainBranch
      ? [
          {
            accessor: 'branchName',
            Header: 'Branch',
            cellProps: { className: 'py-2' },
            Cell: ({ value }) => value || '-'
          }
        ]
      : []),
    {
      accessor: 'sponsorshipType',
      Header: 'Type',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => (
        <SoftBadge bg="info" className="me-1">
          {value || 'Sponsorship'}
        </SoftBadge>
      )
    },
    {
      accessor: 'monthlyCost',
      Header: 'Amount',
      cellProps: { className: 'py-2 fw-medium' },
      Cell: ({ value, row }) => {
        const { donors } = row.original;
        const label = `${formatMoney(value)}/mo`;
        // Single sponsor: jump straight to their giving history
        if (donors?.length === 1 && donors[0]?.id) {
          return (
            <Link to={paths.donorGiving.replace(':id', donors[0].id)}>
              {label}
            </Link>
          );
        }
        return label;
      }
    },
    {
      accessor: 'startDate',
      Header: 'Start Date',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) =>
        value ? format(new Date(value), 'dd MMM yyyy') : '-'
    },
    {
      accessor: 'endDate',
      Header: 'End Date',
      cellProps: { className: 'py-2' },
      Cell: ({ value, row }) => {
        const { ongoing, paidTo } = row.original;
        if (ongoing) {
          return (
            <div>
              <SoftBadge bg="success" pill>
                Ongoing
              </SoftBadge>
              {paidTo && (
                <div className="text-500 fs--2">
                  paid to {format(new Date(paidTo), 'MMM yyyy')}
                </div>
              )}
            </div>
          );
        }
        return value ? format(new Date(value), 'dd MMM yyyy') : '-';
      }
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => (
        <SoftBadge
          bg={
            value === 'active'
              ? 'success'
              : value === 'partial'
              ? 'warning'
              : 'secondary'
          }
        >
          {value === 'active'
            ? 'Fully Sponsored'
            : value === 'partial'
            ? 'Partial'
            : 'Unsponsored'}
        </SoftBadge>
      )
    },
    {
      accessor: 'totalCoverage',
      Header: 'Coverage',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const { totalCoverage } = row.original;
        return (
          <div style={{ width: '100px' }}>
            <ProgressBar
              now={totalCoverage}
              label={`${Math.round(totalCoverage)}%`}
              variant={
                totalCoverage >= 100
                  ? 'success'
                  : totalCoverage > 0
                  ? 'warning'
                  : 'danger'
              }
              style={{ height: '20px' }}
            />
          </div>
        );
      }
    },
    {
      accessor: 'actions',
      Header: 'Actions',
      cellProps: { className: 'py-2 text-end' },
      disableSortBy: true,
      Cell: ({ row }) => (
        <Flex className="gap-1">
          <Button
            size="sm"
            variant="falcon-default"
            onClick={() => {
              setSelectedBeneficiaryId(row.original.beneficiaryId);
              setSelectedTypeId(row.original.beneficiarySponsorshipTypeId);
              setShowDetailModal(true);
            }}
          >
            <FontAwesomeIcon icon="eye" />
          </Button>
          <Button
            size="sm"
            variant="falcon-primary"
            onClick={() => {
              navigate(
                `${paths.createNewSponsorship}?beneficiaryId=${row.original.beneficiaryId}`
              );
            }}
          >
            <FontAwesomeIcon icon="plus" />
          </Button>
        </Flex>
      )
    }
  ];

  return (
    <>
      {/* Summary Stats */}
      <Row className="g-3 mb-3">
        {DASHBOARD_STATUSES.map(st => (
          <Col md={6} lg={3} key={st.name}>
            <StatCard
              icon={st.icon}
              title={st.name}
              value={
                // Each figure stays on one line; a narrow card wraps between
                // figures, never inside "Hafiz 50"
                <span className="fs-1">
                  <span className="text-nowrap">
                    Orphan {stats[st.name]?.orphan ?? 0}
                    <span className="text-400 mx-2">/</span>
                  </span>{' '}
                  <span className="text-nowrap">
                    Hafiz {stats[st.name]?.hafiz ?? 0}
                    {showChildCount && <span className="text-400 mx-2">/</span>}
                  </span>
                  {showChildCount && (
                    <>
                      {' '}
                      <span className="text-nowrap">
                        Child {stats[st.name]?.child ?? 0}
                      </span>
                    </>
                  )}
                </span>
              }
              details={
                stats[st.name]?.countries?.length
                  ? // Same order as the headline: Orphan / Hafiz (/ Child)
                    stats[st.name].countries.map((c, i) => (
                      <span key={c.country}>
                        {i > 0 && ' · '}
                        <span className="text-nowrap">
                          {c.country} {c.orphan} / {c.hafiz}
                          {showChildCount && ` / ${c.child}`}
                        </span>
                      </span>
                    ))
                  : null
              }
              subtitle={
                statusFilter === st.name
                  ? 'Showing these children'
                  : 'Click to filter'
              }
              color={st.color}
              onClick={() =>
                setStatusFilter(statusFilter === st.name ? 'all' : st.name)
              }
              active={statusFilter === st.name}
            />
          </Col>
        ))}
      </Row>

      {/* #2 Filters */}
      <Row className="mb-3">
        <Col>
          <Card className="shadow-none border">
            <Card.Body>
              <FormProvider methods={filterForm}>
                <Row className="g-3 align-items-end">
                  <Col md={6} lg style={FILTER_FIELD_STYLE}>
                    <RHFControl
                      name="donor"
                      label="Donor name"
                      placeholder="Search donor..."
                      noOptional={true}
                    />
                  </Col>
                  <Col md={6} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="branch"
                      label="Branch"
                      options={branchOptions}
                      noOptional={true}
                      isClearable={true}
                      isMulti
                    />
                  </Col>
                  <Col md={6} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="type"
                      label="Sponsorship type"
                      options={typeOptions}
                      noOptional={true}
                      isClearable={true}
                      isMulti
                    />
                  </Col>
                  <Col md={6} lg style={FILTER_FIELD_STYLE}>
                    <RHFSelect
                      name="childStatus"
                      label="Child status"
                      options={childStatusOptions}
                      noOptional={true}
                      isClearable={true}
                      isMulti
                    />
                  </Col>
                  {hasListFilters && (
                    <Col md={4} lg style={FILTER_FIELD_STYLE}>
                      <Button
                        variant="outline-secondary"
                        className="w-100"
                        style={{ height: '38px' }}
                        onClick={() => filterForm.reset()}
                      >
                        Clear Filters
                      </Button>
                    </Col>
                  )}
                </Row>
              </FormProvider>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Table */}
      <Row>
        <Col>
          <CustomAdvanceTable
            data={sponsorships}
            columns={columns}
            title="Sponsorship Management"
            showSearchInput
            searchInputPlaceHolder="Search by beneficiary or donor name..."
            addNew={false}
            loading={isLoading}
            hasExport
            handleExport={() => setShowExportModal(true)}
          />
        </Col>
      </Row>

      <ExportModal
        data={exportData}
        columns={exportColumns}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName="Sponsorship Management"
      />

      {/* Detail Modal */}
      <SponsorshipDetailModal
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedBeneficiaryId(null);
          setSelectedTypeId(null);
        }}
        beneficiaryId={selectedBeneficiaryId}
        sponsorshipTypeId={selectedTypeId}
        onAddSponsor={() => {
          setShowDetailModal(false);
          navigate(
            `${paths.createNewSponsorship}?beneficiaryId=${selectedBeneficiaryId}`
          );
        }}
        onEndSponsorship={handleEndSponsorship}
        onChangePayment={handleChangePaymentClick}
      />

      {/* Donation picker for Change payment */}
      <SelectDonationModal
        show={!!changePaymentFor}
        onHide={() => {
          setChangePaymentFor(null);
          setShowDetailModal(true);
        }}
        onSubmit={handleChangePaymentSubmit}
      />

      <style>
        {`
        .icon-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.15;
        }
        .icon-circle-primary { background-color: var(--falcon-primary); }
        .icon-circle-success { background-color: var(--falcon-success); }
        .icon-circle-warning { background-color: var(--falcon-warning); }
        .icon-circle-danger { background-color: var(--falcon-danger); }
        .icon-circle-secondary { background-color: var(--falcon-secondary); }
        .cursor-pointer { cursor: pointer; }
      `}
      </style>
    </>
  );
};

export default SponsorshipManagement;
